import React, { useEffect, useState } from "react";
import {
  Bell, BellOff, CheckCircle2, AlertCircle,
  Smartphone, Monitor, HelpCircle, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  pushSupported, permissionState, subscribe, unsubscribe,
  currentEndpoint,
} from "@/lib/push";

/**
 * PushNotificationsCard — user-facing toggle + how-to for web push.
 *
 * Shows one of four states:
 *   unsupported — browser doesn't support push (e.g. Safari without Add-to-Home-Screen)
 *   denied      — user previously said "Block"; they need to re-enable in browser settings
 *   active      — subscribed; we show an Unsubscribe button + test instructions
 *   prompt      — default state; big green "Enable notifications" button
 *
 * Always renders a "How to enable" accordion with per-device instructions
 * because people WILL get confused.
 */
export default function PushNotificationsCard({ variant = "card" }) {
  const [status, setStatus] = useState("loading"); // loading | unsupported | denied | active | prompt
  const [busy, setBusy] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const refresh = async () => {
    if (!pushSupported()) { setStatus("unsupported"); return; }
    const perm = permissionState();
    if (perm === "denied") { setStatus("denied"); return; }
    const ep = await currentEndpoint();
    if (ep && perm === "granted") { setStatus("active"); return; }
    setStatus("prompt");
  };
  useEffect(() => { refresh(); }, []);

  const onEnable = async () => {
    setBusy(true);
    try {
      await subscribe();
      toast.success("Push notifications enabled 🔔");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Couldn't enable push.");
      await refresh();
    } finally { setBusy(false); }
  };
  const onDisable = async () => {
    setBusy(true);
    try {
      await unsubscribe();
      toast.success("Push notifications disabled.");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Couldn't disable push.");
    } finally { setBusy(false); }
  };

  const Wrapper = ({ children }) =>
    variant === "inline"
      ? <div className="space-y-3">{children}</div>
      : <div className="loot-card-flat p-5 space-y-4" data-testid="push-card">{children}</div>;

  return (
    <Wrapper>
      <Header status={status} />

      {status === "loading" && (
        <div className="font-mono text-xs text-[#F5F2E8]/60">Checking…</div>
      )}

      {status === "unsupported" && (
        <div className="text-sm text-[#F5F2E8]/80">
          <p>
            This browser can't receive push notifications. On iPhone, open the site in
            <span className="font-display uppercase px-1"> Safari</span> and tap
            <span className="font-display uppercase px-1">Share → Add to Home Screen</span>.
            Then open the app icon from your home screen — push will work from there.
          </p>
        </div>
      )}

      {status === "denied" && (
        <div className="text-sm text-[#F5F2E8]/80 space-y-2">
          <p className="flex items-start gap-2">
            <AlertCircle size={16} className="text-[#E53935] shrink-0 mt-0.5" />
            Notifications are <span className="font-display uppercase text-[#E53935]">blocked</span> in your
            browser settings. To re-enable them, scroll down to "How to enable" and follow the steps for
            your device.
          </p>
        </div>
      )}

      {status === "prompt" && (
        <>
          <p className="text-sm text-[#F5F2E8]/80">
            Get pinged the moment a drop goes live in your city — even when the site is closed. No ads,
            no spam. Turn it off anytime.
          </p>
          <button
            onClick={onEnable}
            disabled={busy}
            className="btn-loot inline-flex items-center gap-2"
            data-testid="push-enable-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />}
            {busy ? "Enabling…" : "Enable notifications"}
          </button>
        </>
      )}

      {status === "active" && (
        <>
          <p className="text-sm text-[#F5F2E8]/80 flex items-start gap-2">
            <CheckCircle2 size={16} className="text-[#76B82A] shrink-0 mt-0.5" />
            You're all set. We'll ping you when a drop goes live, a clue unlocks, or a hunt starts near you.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={onDisable}
              disabled={busy}
              className="btn-ghost text-xs inline-flex items-center gap-1"
              data-testid="push-disable-btn"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <BellOff size={12} />}
              {busy ? "Working…" : "Turn off notifications"}
            </button>
            <span className="font-mono text-[10px] text-[#F5F2E8]/50">
              You can also disable them in your browser settings.
            </span>
          </div>
        </>
      )}

      <div className="pt-3 border-t border-[#F5F2E8]/10">
        <button
          type="button"
          onClick={() => setHelpOpen((v) => !v)}
          className="flex items-center gap-2 text-xs text-[#D7A62A] hover:text-[#76B82A]"
          data-testid="push-help-toggle"
        >
          <HelpCircle size={14} />
          <span className="font-display uppercase tracking-widest">
            {helpOpen ? "Hide instructions" : "How to enable"}
          </span>
        </button>
        {helpOpen && <HowToEnable />}
      </div>
    </Wrapper>
  );
}

function Header({ status }) {
  const map = {
    loading:     { color: "#F5F2E8", Icon: Bell,   label: "Push notifications" },
    unsupported: { color: "#D7A62A", Icon: BellOff, label: "Not supported on this browser" },
    denied:      { color: "#E53935", Icon: BellOff, label: "Notifications are blocked" },
    active:      { color: "#76B82A", Icon: Bell,   label: "Notifications are ON" },
    prompt:      { color: "#D7A62A", Icon: Bell,   label: "Notifications are off" },
  };
  const { color, Icon, label } = map[status] || map.loading;
  return (
    <div className="flex items-center gap-2">
      <Icon size={18} style={{ color }} />
      <div>
        <div className="overline">Browser push</div>
        <div className="font-display uppercase text-xl leading-tight" style={{ color }}>
          {label}
        </div>
      </div>
    </div>
  );
}

function HowToEnable() {
  return (
    <div className="mt-3 space-y-4 text-sm text-[#F5F2E8]/85" data-testid="push-help">
      <DeviceBlock
        icon={<Smartphone size={16} className="text-[#D7A62A]" />}
        title="iPhone / iPad (Safari)"
        steps={[
          "Open thelocalloot.com in Safari (not in another browser).",
          "Tap the Share icon at the bottom (square with arrow up).",
          "Scroll and tap Add to Home Screen → Add.",
          "Open the new app icon from your home screen.",
          "Come back here and tap Enable notifications — tap Allow when prompted.",
        ]}
        note="iOS only lets web apps send push after they're installed to the home screen. One-time setup."
      />
      <DeviceBlock
        icon={<Smartphone size={16} className="text-[#76B82A]" />}
        title="Android (Chrome / Samsung Browser)"
        steps={[
          "Tap Enable notifications above.",
          "When the browser asks, tap Allow.",
          "Optional: tap ⋮ menu → Install app to pin it to your home screen.",
        ]}
      />
      <DeviceBlock
        icon={<Monitor size={16} className="text-[#76B82A]" />}
        title="Desktop (Chrome / Edge / Firefox)"
        steps={[
          "Click Enable notifications above.",
          "Click Allow in the browser permission prompt.",
          "Pings arrive in your OS notification center — even when the browser is minimized.",
        ]}
      />
      <DeviceBlock
        icon={<AlertCircle size={16} className="text-[#E53935] shrink-0" />}
        title="Blocked them by mistake?"
        steps={[
          "iPhone: Settings → Safari → Advanced → Website Data → find thelocalloot.com → Remove. Then come back and try again.",
          "Android Chrome: Tap the 🔒 icon in the address bar → Permissions → Notifications → Allow.",
          "Desktop Chrome: Click the 🔒 icon in the address bar → Notifications → Allow → refresh the page.",
        ]}
      />
    </div>
  );
}

function DeviceBlock({ icon, title, steps, note }) {
  return (
    <div className="border border-[#F5F2E8]/10 p-3">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <div className="font-display uppercase tracking-widest text-sm">{title}</div>
      </div>
      <ol className="list-decimal list-inside space-y-1 text-sm text-[#F5F2E8]/80">
        {steps.map((s, i) => <li key={i}>{s}</li>)}
      </ol>
      {note && (
        <div className="mt-2 font-mono text-[11px] text-[#F5F2E8]/55">
          {note}
        </div>
      )}
    </div>
  );
}
